/* eslint-disable react/prop-types */
import React, {useState, useEffect, useLayoutEffect} from 'react';
import {useMediaQuery} from 'react-responsive';
import {Link} from 'react-router-dom';
import {Button, Fab, Drawer, Chip} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MenuIcon from '@mui/icons-material/Menu';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ForumIcon from '@mui/icons-material/Forum';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import styled, {keyframes} from 'styled-components';
import Tab from '../../Components/tab';
import Footer from '../../Components/Footer';
import logo from '../../Assets/logo.png';

/**
 * Demo interativa do Fiinc (ireit.com.br/financas/demo) — item 3 do plano em
 * docs/FIINC_LANDING_SITE.md (enterpriseireit). Simula, com dado 100% fake e
 * fixo (sem nenhuma chamada de rede), o fluxo: importar extrato →
 * classificação por IA → fechar o mês → conversar com o Fiinc. Objetivo
 * explícito do usuário: dar sensação de segurança pra quem ainda não confia
 * em subir extrato bancário num app novo — por isso o aviso "nada foi
 * enviado" aparece mais de uma vez.
 */

const fadeUp = keyframes`
  from { transform: translateY(14px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`;
const FadeUp = styled.div`
  animation: ${fadeUp} 0.45s ease-out both;
  animation-delay: ${p => p.delay || '0s'};
`;

const C = {
  azul: '#06448e',
  azulD: '#04316a',
  laranja: '#FF6600',
  laranjaD: '#cc4400',
  roxo: '#7c53e3',
  verde: '#059669',
  rosa: '#db2777',
  bg: '#f4f6fa',
  text: '#1a1a2e',
  muted: '#6b7280',
  white: '#ffffff',
};

const NAV_HEIGHT = 60;

const ETAPAS = [
  {titulo: 'Importar', icon: CloudUploadIcon},
  {titulo: 'Classificar', icon: AutoAwesomeIcon},
  {titulo: 'Fechar o mês', icon: EventAvailableIcon},
  {titulo: 'Conversar', icon: ForumIcon},
];

const ARQUIVOS_FAKE = [
  {nome: 'fatura_nubank_agosto.pdf', tipo: 'Fatura de cartão'},
  {nome: 'extrato_bradesco_agosto.csv', tipo: 'Extrato de conta'},
];

const TRANSACOES_FAKE = [
  {desc: 'Aluguel', valor: -1450, cat: 'Moradia', cor: C.azul},
  {desc: 'iFood', valor: -47.9, cat: 'Alimentação', cor: C.laranja},
  {desc: 'Posto Shell', valor: -180, cat: 'Transporte', cor: C.roxo},
  {desc: 'Netflix', valor: -39.9, cat: 'Assinaturas', cor: C.rosa},
  {desc: 'Farmácia São João', valor: -62.3, cat: 'Saúde', cor: C.verde},
  {desc: 'Salário', valor: 4800, cat: 'Renda', cor: '#0d9488'},
];

const CATEGORIAS_MES = [
  {label: 'Moradia', pct: 38, cor: C.azul},
  {label: 'Alimentação', pct: 22, cor: C.laranja},
  {label: 'Transporte', pct: 14, cor: C.roxo},
  {label: 'Assinaturas', pct: 8, cor: C.rosa},
  {label: 'Saúde', pct: 6, cor: C.verde},
  {label: 'Outros', pct: 12, cor: C.muted},
];

const SUGESTOES_CHAT = [
  {
    pergunta: 'Vale a pena comprar um iPhone parcelado em 6x?',
    resposta: (
      <>
        Olhando o orçamento de vocês, dá pra parcelar sem estourar a meta de{' '}
        <b>Lazer</b> — mas sobra menos folga nos próximos 6 meses. Um caminho:
        separar <b>R$ 350/mês numa caixinha</b> em vez de parcelar, e comprar à
        vista em 4 meses, sem juros e sem comprometer a reserva.
      </>
    ),
  },
  {
    pergunta: 'Como está nossa saúde financeira?',
    resposta: (
      <>
        Vocês estão no perfil{' '}
        <b style={{color: C.verde}}>&quot;No Caminho Certo&quot;</b> — a sobra
        do mês vem crescendo há 3 meses seguidos. Pra chegar em &quot;Construtor
        de Patrimônio&quot;, falta consolidar mais um mês de reserva de
        emergência.
      </>
    ),
  },
  {
    pergunta: 'Quanto sobrou esse mês?',
    resposta: (
      <>
        Sobraram <b>R$ 612,40</b> — 12% a mais que o mês passado. Se mantiverem
        esse ritmo, dá pra fechar a reserva de emergência em cerca de 4 meses.
      </>
    ),
  },
];

const StepDot = ({ativo, feito, icon: Icon, titulo, mobile}) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 6,
      flex: 1,
    }}
  >
    <div
      style={{
        width: mobile ? 34 : 42,
        height: mobile ? 34 : 42,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: ativo || feito ? C.azul : C.white,
        border: `2px solid ${ativo || feito ? C.azul : '#dbe1eb'}`,
        transition: 'all 0.2s',
      }}
    >
      {feito && !ativo ? (
        <CheckCircleIcon style={{color: '#fff', fontSize: mobile ? 18 : 22}} />
      ) : (
        <Icon
          style={{
            color: ativo ? '#fff' : '#9aa5b6',
            fontSize: mobile ? 16 : 20,
          }}
        />
      )}
    </div>
    {!mobile && (
      <span
        style={{
          fontSize: 11.5,
          fontWeight: ativo ? 700 : 500,
          color: ativo ? C.azul : C.muted,
        }}
      >
        {titulo}
      </span>
    )}
  </div>
);

const CardShell = ({children}) => (
  <FadeUp
    style={{
      background: C.white,
      borderRadius: 20,
      boxShadow: '0 12px 40px rgba(20,30,60,0.10)',
      padding: '28px 24px',
      minHeight: 380,
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    {children}
  </FadeUp>
);

const StepHeading = ({eyebrow, titulo, texto}) => (
  <div style={{marginBottom: 22}}>
    <p
      style={{
        color: C.laranja,
        fontWeight: 800,
        fontSize: 12,
        letterSpacing: 1,
        textTransform: 'uppercase',
        margin: '0 0 6px',
      }}
    >
      {eyebrow}
    </p>
    <h2
      style={{margin: '0 0 6px', fontSize: 22, fontWeight: 800, color: C.text}}
    >
      {titulo}
    </h2>
    <p style={{margin: 0, fontSize: 13.5, color: C.muted, lineHeight: 1.5}}>
      {texto}
    </p>
  </div>
);

// ─── Passo 1: Importar ──────────────────────────────────────────────────────
const PassoImportar = ({onAvancar}) => {
  const [carregando, setCarregando] = useState(false);
  const [arquivos, setArquivos] = useState([]);

  const simular = () => {
    if (carregando || arquivos.length > 0) return;
    setCarregando(true);
    setTimeout(() => {
      setArquivos(ARQUIVOS_FAKE);
      setCarregando(false);
    }, 900);
  };

  return (
    <CardShell>
      <StepHeading
        eyebrow="Passo 1 de 4"
        titulo="Solte seus extratos aqui"
        texto="CSV de Nubank, Flash, Bradesco ou fatura em PDF — pode misturar formatos e soltar vários arquivos de uma vez."
      />
      <div
        onClick={simular}
        style={{
          border: `2px dashed ${arquivos.length ? C.verde : '#c7d0e0'}`,
          borderRadius: 14,
          padding: '32px 20px',
          textAlign: 'center',
          cursor: arquivos.length ? 'default' : 'pointer',
          background: arquivos.length ? '#f0fbf5' : C.bg,
          transition: 'all 0.2s',
        }}
      >
        <CloudUploadIcon
          style={{
            fontSize: 34,
            color: arquivos.length ? C.verde : C.azul,
            marginBottom: 8,
          }}
        />
        <p style={{margin: 0, fontWeight: 700, fontSize: 14, color: C.text}}>
          {carregando
            ? 'Importando e detectando o formato...'
            : arquivos.length
            ? 'Importado com sucesso!'
            : 'Clique pra simular uma importação'}
        </p>
        <p style={{margin: '4px 0 0', fontSize: 12, color: C.muted}}>
          Isso é só uma simulação — nenhum arquivo é enviado de verdade.
        </p>
      </div>

      {arquivos.length > 0 && (
        <div style={{marginTop: 16}}>
          {arquivos.map((a, i) => (
            <FadeUp
              key={a.nome}
              delay={`${i * 0.12}s`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                background: C.bg,
                borderRadius: 10,
                padding: '10px 12px',
                marginBottom: 8,
              }}
            >
              <DescriptionIcon style={{color: C.azul, fontSize: 20}} />
              <div style={{flex: 1, minWidth: 0}}>
                <p
                  style={{
                    margin: 0,
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: C.text,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {a.nome}
                </p>
                <p style={{margin: 0, fontSize: 11, color: C.muted}}>
                  {a.tipo}
                </p>
              </div>
              <CheckCircleIcon style={{color: C.verde, fontSize: 18}} />
            </FadeUp>
          ))}
        </div>
      )}

      <div style={{flex: 1}} />
      <Button
        fullWidth
        variant="contained"
        endIcon={<ArrowForwardIcon />}
        disabled={arquivos.length === 0}
        onClick={onAvancar}
        style={{
          marginTop: 20,
          textTransform: 'none',
          fontWeight: 700,
          borderRadius: 12,
          padding: '11px 0',
        }}
        sx={{bgcolor: C.azul, ':hover': {bgcolor: C.azulD}}}
      >
        {arquivos.length
          ? 'Ver classificação automática'
          : 'Importe um arquivo pra continuar'}
      </Button>
    </CardShell>
  );
};

// ─── Passo 2: Classificar ───────────────────────────────────────────────────
const PassoClassificar = ({onAvancar}) => (
  <CardShell>
    <StepHeading
      eyebrow="Passo 2 de 4"
      titulo="A IA já classificou tudo"
      texto="Cada transação cai na categoria certa sozinha — fatura em PDF passa por uma revisão rápida antes de valer de verdade."
    />
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        background: '#e9f6ee',
        borderRadius: 12,
        padding: '10px 12px',
        marginBottom: 14,
      }}
    >
      <CheckCircleIcon style={{color: C.verde, fontSize: 20}} />
      <span style={{fontSize: 12.5, color: C.text, fontWeight: 600}}>
        6 transações classificadas automaticamente
      </span>
    </div>
    <div style={{flex: 1}}>
      {TRANSACOES_FAKE.map((t, i) => (
        <FadeUp
          key={t.desc}
          delay={`${i * 0.08}s`}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '9px 2px',
            borderBottom:
              i < TRANSACOES_FAKE.length - 1 ? '1px solid #eef1f6' : 'none',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              minWidth: 0,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: t.cor,
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontSize: 13,
                color: C.text,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {t.desc}
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexShrink: 0,
            }}
          >
            <Chip
              label={t.cat}
              size="small"
              style={{
                background: `${t.cor}1a`,
                color: t.cor,
                fontWeight: 700,
                fontSize: 10.5,
                height: 20,
              }}
            />
            <span
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: t.valor > 0 ? C.verde : C.text,
                minWidth: 68,
                textAlign: 'right',
              }}
            >
              {t.valor > 0 ? '+' : '-'} R${' '}
              {Math.abs(t.valor).toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
              })}
            </span>
          </div>
        </FadeUp>
      ))}
    </div>
    <Button
      fullWidth
      variant="contained"
      endIcon={<ArrowForwardIcon />}
      onClick={onAvancar}
      style={{
        marginTop: 20,
        textTransform: 'none',
        fontWeight: 700,
        borderRadius: 12,
        padding: '11px 0',
      }}
      sx={{bgcolor: C.azul, ':hover': {bgcolor: C.azulD}}}
    >
      Fechar o mês
    </Button>
  </CardShell>
);

// ─── Passo 3: Fechar o mês ──────────────────────────────────────────────────
const PassoFechar = ({onAvancar}) => (
  <CardShell>
    <StepHeading
      eyebrow="Passo 3 de 4"
      titulo="Fechamento guiado do mês"
      texto="Um passo a passo curto revisa categorias, saúde financeira e objetivos — e compara com a meta que vocês definiram no mês anterior."
    />
    <div style={{marginBottom: 18}}>
      {CATEGORIAS_MES.map(c => (
        <div key={c.label} style={{marginBottom: 9}}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 11.5,
              color: C.muted,
              marginBottom: 3,
            }}
          >
            <span>{c.label}</span>
            <span>{c.pct}%</span>
          </div>
          <div
            style={{
              height: 6,
              borderRadius: 4,
              background: '#eef1f6',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${c.pct}%`,
                height: '100%',
                background: c.cor,
                borderRadius: 4,
              }}
            />
          </div>
        </div>
      ))}
    </div>
    <div
      style={{
        background: '#f4f1fd',
        borderRadius: 12,
        padding: 14,
        display: 'flex',
        gap: 10,
        alignItems: 'flex-start',
        marginBottom: 8,
      }}
    >
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: '50%',
          background: C.roxo,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <span style={{fontSize: 15}}>❤️</span>
      </div>
      <div>
        <p style={{margin: 0, fontSize: 12.5, fontWeight: 700, color: C.text}}>
          Saúde financeira: No Caminho Certo
        </p>
        <p
          style={{
            margin: '2px 0 0',
            fontSize: 11.5,
            color: C.muted,
            lineHeight: 1.4,
          }}
        >
          A sobra do mês vem crescendo — como um exame de sangue, o que importa
          é o formato da linha ao longo do tempo, não um mês isolado.
        </p>
      </div>
    </div>
    <div style={{flex: 1}} />
    <Button
      fullWidth
      variant="contained"
      endIcon={<ArrowForwardIcon />}
      onClick={onAvancar}
      style={{
        marginTop: 12,
        textTransform: 'none',
        fontWeight: 700,
        borderRadius: 12,
        padding: '11px 0',
      }}
      sx={{bgcolor: C.azul, ':hover': {bgcolor: C.azulD}}}
    >
      Concluir fechamento
    </Button>
  </CardShell>
);

// ─── Passo 4: Conversar ─────────────────────────────────────────────────────
const PassoConversar = () => {
  const [transcript, setTranscript] = useState([]);
  const [digitando, setDigitando] = useState(false);
  const usadas = transcript.map(t => t.pergunta);
  const disponiveis = SUGESTOES_CHAT.filter(s => !usadas.includes(s.pergunta));

  const perguntar = s => {
    setDigitando(true);
    setTimeout(() => {
      setTranscript(prev => [...prev, s]);
      setDigitando(false);
    }, 900);
  };

  return (
    <CardShell>
      <StepHeading
        eyebrow="Passo 4 de 4"
        titulo="Converse com o Fiinc"
        texto="Pergunte qualquer coisa sobre o dinheiro da família — as respostas usam só o dado real do grupo, nunca invenção."
      />
      <div style={{flex: 1, overflowY: 'auto', marginBottom: 12}}>
        {transcript.length === 0 && !digitando && (
          <p
            style={{
              fontSize: 12.5,
              color: C.muted,
              textAlign: 'center',
              marginTop: 20,
            }}
          >
            Escolha uma pergunta abaixo pra começar.
          </p>
        )}
        {transcript.map((t, i) => (
          <FadeUp
            key={t.pergunta}
            delay={`${i * 0.05}s`}
            style={{marginBottom: 16}}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginBottom: 8,
              }}
            >
              <div
                style={{
                  background: C.azul,
                  color: '#fff',
                  borderRadius: '14px 14px 2px 14px',
                  padding: '9px 13px',
                  fontSize: 12.5,
                  maxWidth: '80%',
                }}
              >
                {t.pergunta}
              </div>
            </div>
            <div style={{display: 'flex', gap: 8, alignItems: 'flex-start'}}>
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  background: C.laranja,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <AutoAwesomeIcon style={{color: '#fff', fontSize: 13}} />
              </div>
              <div
                style={{
                  background: C.bg,
                  borderRadius: '2px 14px 14px 14px',
                  padding: '9px 13px',
                  fontSize: 12.5,
                  color: C.text,
                  lineHeight: 1.5,
                  maxWidth: '85%',
                }}
              >
                {t.resposta}
              </div>
            </div>
          </FadeUp>
        ))}
        {digitando && (
          <div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                background: C.laranja,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AutoAwesomeIcon style={{color: '#fff', fontSize: 13}} />
            </div>
            <span style={{fontSize: 12, color: C.muted, fontStyle: 'italic'}}>
              Fiinc está digitando...
            </span>
          </div>
        )}
      </div>

      {disponiveis.length > 0 ? (
        <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
          {disponiveis.map(s => (
            <button
              key={s.pergunta}
              onClick={() => perguntar(s)}
              disabled={digitando}
              style={{
                textAlign: 'left',
                border: `1px solid ${C.azul}30`,
                background: '#fff',
                color: C.azul,
                borderRadius: 10,
                padding: '9px 12px',
                fontSize: 12.5,
                fontWeight: 600,
                cursor: digitando ? 'default' : 'pointer',
                opacity: digitando ? 0.5 : 1,
              }}
            >
              {s.pergunta}
            </button>
          ))}
        </div>
      ) : (
        <div
          style={{
            textAlign: 'center',
            padding: '14px 10px 4px',
            borderTop: '1px solid #eef1f6',
          }}
        >
          <p style={{margin: '0 0 12px', fontSize: 12.5, color: C.muted}}>
            No app de verdade você pode perguntar qualquer coisa — não só essas
            3.
          </p>
          <Button
            component={Link}
            to="/cadastro_financas"
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            style={{textTransform: 'none', fontWeight: 700, borderRadius: 12}}
            sx={{bgcolor: C.laranja, ':hover': {bgcolor: C.laranjaD}}}
          >
            Criar minha conta grátis
          </Button>
        </div>
      )}
    </CardShell>
  );
};

const FinancasDemo = () => {
  const isSmall = useMediaQuery({query: '(max-width: 750.98px)'});
  const isVerySmall = useMediaQuery({query: '(max-width:450.00px)'});
  const mobile = isSmall || isVerySmall;
  const [toggleBar, setToggleBar] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [passo, setPasso] = useState(0);

  useEffect(() => {
    window.scrollTo({top: 0, behavior: 'smooth'});
  }, []);

  useEffect(() => {
    const t = () => setIsVisible(window.pageYOffset > 50);
    window.addEventListener('scroll', t);
    return () => window.removeEventListener('scroll', t);
  }, []);

  function useWindowSize() {
    const [size, setSize] = useState([0, 0]);
    useLayoutEffect(() => {
      function updateSize() {
        setSize([window.innerWidth, window.innerHeight]);
      }
      window.addEventListener('resize', updateSize);
      updateSize();
      return () => window.removeEventListener('resize', updateSize);
    }, []);
    return size;
  }
  // eslint-disable-next-line no-unused-vars
  const [width] = useWindowSize();

  const Passos = [PassoImportar, PassoClassificar, PassoFechar, PassoConversar];
  const PassoAtual = Passos[passo];

  return (
    <div
      style={{
        overflowX: 'hidden',
        width: '100%',
        background: C.bg,
        minHeight: '100vh',
      }}
    >
      {isVisible && (
        <Fab
          onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}
          style={{position: 'fixed', bottom: 20, left: 20, zIndex: 900}}
          size="small"
          color="primary"
          aria-label="voltar ao topo"
        >
          <ArrowUpwardIcon />
        </Fab>
      )}

      {mobile ? (
        <div
          style={{
            background: C.azul,
            height: NAV_HEIGHT,
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            position: 'fixed',
            zIndex: 999,
            top: 0,
            left: 0,
            width: '100%',
          }}
        >
          <Button
            component={Link}
            to="/"
            style={{
              width: 95,
              height: 47,
              marginLeft: 12,
              backgroundImage: `url(${logo})`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
            }}
          />
          <MenuIcon
            style={{color: '#ffffff', marginRight: 15}}
            onClick={() => setToggleBar(true)}
          />
        </div>
      ) : (
        <Tab />
      )}
      {mobile && (
        <Drawer
          anchor="top"
          open={toggleBar}
          onClose={() => setToggleBar(false)}
        >
          <Tab setToggleBar={setToggleBar} />
        </Drawer>
      )}

      <div
        style={{
          maxWidth: 640,
          margin: '0 auto',
          padding: `${NAV_HEIGHT + 32}px 20px 60px`,
        }}
      >
        <div style={{textAlign: 'center', marginBottom: 28}}>
          <Chip
            icon={<ShieldOutlinedIcon />}
            label="Demonstração — nenhum dado real é usado ou enviado"
            sx={{'& .MuiChip-icon': {color: C.azul}}}
            style={{
              background: '#e6effa',
              color: C.azul,
              fontWeight: 700,
              marginBottom: 14,
            }}
          />
          <h1
            style={{
              fontSize: mobile ? 24 : 28,
              fontWeight: 800,
              color: C.text,
              margin: '0 0 8px',
            }}
          >
            Veja o Fiinc funcionando
          </h1>
          <p style={{fontSize: 14, color: C.muted, margin: 0}}>
            Quatro passos, dado fictício, zero risco — pra você sentir como é
            usar antes de criar sua conta.
          </p>
        </div>

        <div style={{display: 'flex', marginBottom: 26, padding: '0 4px'}}>
          {ETAPAS.map((e, i) => (
            <StepDot
              key={e.titulo}
              icon={e.icon}
              titulo={e.titulo}
              ativo={i === passo}
              feito={i < passo}
              mobile={mobile}
            />
          ))}
        </div>

        <PassoAtual onAvancar={() => setPasso(p => Math.min(p + 1, 3))} />

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 16,
          }}
        >
          <Button
            onClick={() => setPasso(p => Math.max(p - 1, 0))}
            disabled={passo === 0}
            startIcon={<ArrowBackIcon />}
            style={{
              textTransform: 'none',
              fontWeight: 600,
              color: passo === 0 ? '#c3cad6' : C.muted,
            }}
          >
            Voltar
          </Button>
          {passo < 3 && (
            <Button
              onClick={() => setPasso(p => Math.min(p + 1, 3))}
              endIcon={<ArrowForwardIcon />}
              style={{textTransform: 'none', fontWeight: 600, color: C.azul}}
            >
              Pular passo
            </Button>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FinancasDemo;
